import { FormsModule } from '@angular/forms';
import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, effect, ElementRef, inject, input, InputSignal, Signal, viewChild } from '@angular/core';

/**
 * interfaces
 */
import { ITreeNode } from '../../interfaces';

/**
 * utilities
 */
import { flattenTree } from '../../utilities';

/**
 * components
 */
import { TreeComponent } from '../tree/tree.component';
import { ButtonDirective } from '@shared/directives/button';
import { FormCheckFieldDirective } from '@forms/directives/form-check-field/form-check-field.directive';
import { ListGroupDirective } from "@shared/directives/list-groupe/list-group.directive";
import { ListGroupItemDirective } from "@shared/directives/list-groupe/list-group-item.directive";
import { StopPropagationDirective } from "@shared/directives/stop-propagation/stop-propagation.directive";

/**
 * @description A recursive subcomponent responsible for rendering an individual tree node and its children.
 */
@Component({
  selector: 'app-tree-node',
  templateUrl: './tree-node.component.html',
  styleUrl: './tree-node.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    NgTemplateOutlet,
    /**
     * directives
     */
    ButtonDirective,
    FormCheckFieldDirective,
    ListGroupDirective,
    ListGroupItemDirective,
    StopPropagationDirective
],
})
export class TreeNodeComponent {

  /**
   * @description Direct reference to the host TreeComponent. 
   * Used to access templates, signals, and shared state without input drilling.
   */
  public readonly tree: TreeComponent = inject(TreeComponent);

  /**
   * @description The tree node data object to render.
   */
  public readonly node: InputSignal<ITreeNode> = input.required<ITreeNode>();

  /**
   * @description Computed signal to check if this specific node is selected.
   */
  public readonly isSelected: Signal<boolean> = computed(() => {
    return this.tree.treeState.isSelected(this.node(), this.tree.selection());
  });

  /**
   * @description Computed signal determining if the node can be interacted with based on selection mode.
   */
  public selectionModeEnabled: Signal<boolean> = computed(() => this.tree.selectionMode() != null && this.node().selectable !== false)

  /**
   * @description Alias to the parent's selection mode signal for template access.
   */
  public readonly selectionModeCheckBox: Signal<boolean> = this.tree.selectionModeCheckBox

  /**
   * @description Reference to the native checkbox input (when in checkbox mode).
   * Used to imperatively drive `.checked` and `.indeterminate` properties — the
   * `FormCheckFieldDirective` binds `[attr.checked]` (the *attribute*), which does NOT
   * update the live DOM property after first render, so we drive the property from signals.
   */
  public readonly checkboxRef = viewChild<ElementRef<HTMLInputElement>>('checkboxRef');

  /**
   * @description Indeterminate state for checkbox mode — true when only some (selectable)
   * descendants are in the current selection. Pure derived signal — no stored state.
   */
  public readonly isIndeterminate: Signal<boolean> = computed(() => {
    if (!this.tree.selectionModeCheckBox()) return false;

    const children = this.node().children;
    if (!children?.length) return false;

    const selection = (this.tree.selection() as ITreeNode[] | null) ?? [];
    if (!selection.length) return false;

    const descendants = flattenTree(children).filter(n => n.selectable !== false);
    const selectedKeys = new Set(selection.map(n => n.key));
    const matched = descendants.filter(n => selectedKeys.has(n.key)).length;

    return matched > 0 && matched < descendants.length;
  });

  /**
   * @description Sync `isSelected()` / `isIndeterminate()` signals onto the native
   * checkbox DOM properties. Runs every time either signal changes. This is the
   * single point that keeps the visual checkbox in lockstep with the selection state.
   */
  constructor() {
    effect(() => {
      const el = this.checkboxRef()?.nativeElement;
      if (!el) return;
      el.checked = this.isSelected();
      el.indeterminate = this.isIndeterminate();
    });
  }

  /**
   * @description Toggles the expanded/collapsed state of the current node.
   * @param event The DOM mouse event.
   */
  public toggleExpand(event: Event): void {

    // stopPropagation
    event.stopPropagation();

    // get currentNode
    const currentNode = this.node();
    
    // 
    if (currentNode.leaf) return;

    // lazy load mode
    const hasNoChildren = !Array.isArray(currentNode.children) || currentNode.children.length === 0;
    if (hasNoChildren) {
      if (currentNode.loading) return;
      this.tree.onLazyLoad.emit({ node: currentNode });
      return;
    }

    // normal mode
    currentNode.expanded = !currentNode.expanded;
    if (currentNode.expanded) {
      this.tree.onNodeExpand.emit(currentNode);
    } else {
      this.tree.onNodeCollapse.emit(currentNode);
    }

  }

  /**
   * @description Triggered when the node body (not the toggler) is clicked.
   */
  public onNodeClick(event: MouseEvent): void {
    // Stop propagation so parent nodes don't trigger their click event
    event.stopPropagation();
    this.tree.handleNodeClick(this.node(), event);
  }

  /**
   * @description Handles double-click events on the node.
   * @param event MouseEvent object.
   */
  public onNodeDoubleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.tree.onNodeDoubleClick.emit(this.node());
  }

  /**
   * @description Handles keyboard navigation for accessibility (a11y).
   */
  public onKeyDown(event: KeyboardEvent): void {
    // const currentNode = this.node();

    // switch (event.key) {
    //   // 1. التحديد باستخدام Enter أو المسافة (Space)
    //   case 'Enter':
    //   case ' ':
    //     event.preventDefault(); // منع نزول الصفحة للأسفل عند ضغط المسافة
    //     this.tree.handleNodeClick(currentNode, new MouseEvent('click'));
    //     break;

    //   // 2. الفتح باستخدام السهم الأيمن (إذا كانت مغلقة وليست ورقة)
    //   case 'ArrowRight':
    //     if (!currentNode.leaf && !currentNode.expanded) {
    //       event.preventDefault();
    //       this.toggleExpand(new Event('click'));
    //     }
    //     break;

    //   // 3. الإغلاق باستخدام السهم الأيسر (إذا كانت مفتوحة وليست ورقة)
    //   case 'ArrowLeft':
    //     if (!currentNode.leaf && currentNode.expanded) {
    //       event.preventDefault();
    //       this.toggleExpand(new Event('click'));
    //     }
    //     break;
    // }
  }

}
